'use client'

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Client, ClientInput, ClientType } from '@garage/shared'
import { clientInputSchema, CLIENT_TYPES, CLIENT_TYPE_LABELS } from '@garage/shared'
import {
  Dialog,
  TextField,
  Checkbox,
  Button,
  Segmented,
  useSnackbar,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

interface ClientFormDialogProps {
  open: boolean
  client?: Client
  onClose: () => void
  /**
   * Called with the newly created customer. Lets a caller act on it — the
   * jobcard editor selects it immediately, so adding a customer mid-jobcard
   * does not mean going to the Clients page and coming back.
   */
  onCreated?: (client: Client) => void
}

interface FormState {
  type: ClientType
  firstName: string
  lastName: string
  companyName: string
  trn: string
  tradeLicence: string
  billingAddress: string
  phone: string
  whatsappPhone: string | null
  notes: string | null
  sameAsPhone: boolean
}

export function ClientFormDialog({ open, client, onClose, onCreated }: ClientFormDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const isCreate = !client

  const [formState, setFormState] = useState<FormState>({
    type: client?.type ?? 'individual',
    firstName: client?.firstName ?? '',
    lastName: client?.lastName ?? '',
    companyName: client?.companyName ?? '',
    trn: client?.trn ?? '',
    tradeLicence: client?.tradeLicence ?? '',
    billingAddress: client?.billingAddress ?? '',
    phone: client?.phone ?? '',
    whatsappPhone: client?.whatsappPhone ?? null,
    notes: client?.notes ?? null,
    sameAsPhone: !client || client.whatsappPhone === null,
  })

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormState({
        type: client?.type ?? 'individual',
        firstName: client?.firstName ?? '',
        lastName: client?.lastName ?? '',
        companyName: client?.companyName ?? '',
        trn: client?.trn ?? '',
        tradeLicence: client?.tradeLicence ?? '',
        billingAddress: client?.billingAddress ?? '',
        phone: client?.phone ?? '',
        whatsappPhone: client?.whatsappPhone ?? null,
        notes: client?.notes ?? null,
        sameAsPhone: !client || client.whatsappPhone === null,
      })
      setFieldErrors({})
    }
  }, [open, client])

  const mutation = useMutation({
    mutationFn: async (input: ClientInput) => {
      if (isCreate) {
        return api.clients.create(input)
      } else {
        return api.clients.update(client!.id, input)
      }
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: qk.clients() })
      queryClient.invalidateQueries({ queryKey: qk.dashboard })
      if (!isCreate) {
        queryClient.invalidateQueries({ queryKey: qk.client(client!.id) })
      }
      snackbar.success(
        isCreate ? t('clientForm.clientAddedSuccessfully') : t('clientForm.clientUpdatedSuccessfully')
      )
      if (isCreate) onCreated?.(saved)
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const input: ClientInput = {
      type: formState.type,
      firstName: formState.firstName,
      lastName: formState.lastName,
      companyName: formState.companyName,
      trn: formState.trn,
      tradeLicence: formState.tradeLicence,
      billingAddress: formState.billingAddress,
      phone: formState.phone,
      whatsappPhone: formState.sameAsPhone ? null : formState.whatsappPhone,
      notes: formState.notes,
    }

    const result = clientInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        const key = issue.path.join('.')
        errors[key] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    mutation.mutate(input)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('clients.addClient') : t('clients.editClient')}
      size="md"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit}
            loading={mutation.isPending}
          >
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Segmented
          label={t('clientForm.customerType')}
          options={CLIENT_TYPES.map((type) => ({
            value: type,
            label: t(`clientForm.types.${type}`, CLIENT_TYPE_LABELS[type]),
          }))}
          value={formState.type}
          onChange={(type) => setFormState((s) => ({ ...s, type: type as ClientType }))}
        />

        {formState.type === 'individual' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              label={t('clientForm.firstName')}
              value={formState.firstName}
              onChange={(v) =>
                setFormState((s) => ({ ...s, firstName: v }))
              }
              error={fieldErrors.firstName}
              fullWidth
            />
            <TextField
              label={t('clientForm.lastNameOptional')}
              value={formState.lastName}
              onChange={(v) =>
                setFormState((s) => ({ ...s, lastName: v }))
              }
              error={fieldErrors.lastName}
              fullWidth
            />
          </div>
        ) : (
          <div className="space-y-4">
            <TextField
              label={t('clients.company')}
              value={formState.companyName}
              onChange={(v) =>
                setFormState((s) => ({ ...s, companyName: v }))
              }
              error={fieldErrors.companyName}
              fullWidth
            />
            <TextField
              label={t('clientForm.trnLabel')}
              value={formState.trn}
              onChange={(v) =>
                setFormState((s) => ({ ...s, trn: v }))
              }
              error={fieldErrors.trn}
              placeholder={t('clientForm.trnPlaceholder')}
              fullWidth
            />
            <TextField
              label={t('clientDetail.tradeLicence')}
              value={formState.tradeLicence}
              onChange={(v) =>
                setFormState((s) => ({ ...s, tradeLicence: v }))
              }
              error={fieldErrors.tradeLicence}
              fullWidth
            />
            <TextField
              label={t('clientDetail.billingAddress')}
              value={formState.billingAddress}
              onChange={(v) =>
                setFormState((s) => ({ ...s, billingAddress: v }))
              }
              error={fieldErrors.billingAddress}
              multiline
              rows={2}
              placeholder={t('clientForm.billingAddressPlaceholder')}
              fullWidth
            />
          </div>
        )}

        <TextField
          label={t('clientForm.phoneOptional')}
          value={formState.phone}
          onChange={(v) => setFormState((s) => ({ ...s, phone: v }))}
          error={fieldErrors.phone}
          fullWidth
        />

        <div className="space-y-2">
          <Checkbox
            checked={formState.sameAsPhone}
            onChange={(checked) =>
              setFormState((s) => ({ ...s, sameAsPhone: checked }))
            }
            label={t('clientForm.whatsappSameAsPhone')}
          />

          {!formState.sameAsPhone && (
            <TextField
              label={t('clientForm.whatsappNumber')}
              value={formState.whatsappPhone ?? ''}
              onChange={(v) =>
                setFormState((s) => ({ ...s, whatsappPhone: v }))
              }
              error={fieldErrors.whatsappPhone}
              fullWidth
            />
          )}
        </div>

        <TextField
          label={t('clientDetail.notes')}
          value={formState.notes ?? ''}
          onChange={(v) =>
            setFormState((s) => ({ ...s, notes: v || null }))
          }
          error={fieldErrors.notes}
          multiline
          rows={3}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
